import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { ProductBadge } from './product-badge';
import { stretchedLinkSx } from '../layout/stretched-link';
import { isBrowserHandledClick } from './browser-click';

import type { MouseEvent } from 'react';
import type { ProductDirectoryJob } from './product-directory-api';

type JobRowProps = {
  job: ProductDirectoryJob;
  isFavorite: boolean;
  onToggleFavorite: (token: string) => void;
  /** The open product's own badge, so a row says which product the action lands in. */
  badge: string;
  category: string;
  /** Lets a host inside the same product keep the action in the SPA instead of reloading it. */
  onNavigate?: (path: string) => void;
};

function JobRow({ job, isFavorite, onToggleFavorite, badge, category, onNavigate }: JobRowProps) {
  // A star that is set is state worth seeing; an unset one is an action, offered on approach.
  let toggleOpacity = 0;
  if (isFavorite) {
    toggleOpacity = 1;
  }

  const handleClick = (event: MouseEvent) => {
    if (onNavigate !== undefined && !isBrowserHandledClick(event)) {
      event.preventDefault();
      onNavigate(job.path);
    }
  };

  return (
    // One row, one hover surface: the link covers the whole row through its ::after and the
    // star rides on top of it, so the two never read as separate items side by side.
    <Box
      component="li"
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        borderRadius: '6px',
        mx: -0.75,
        px: 0.75,
        py: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover [data-action-toggle]': { opacity: 1 },
        '&:focus-within': { bgcolor: 'action.hover' },
        '&:focus-within [data-action-toggle]': { opacity: 1 },
      }}
    >
      <ProductBadge badge={badge} category={category} size={22} />
      <Box
        component="a"
        href={job.path}
        onClick={handleClick}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.primary',
          textDecoration: 'none',
          typography: 'body2',
          ...stretchedLinkSx({ radius: '6px' }),
        }}
      >
        {job.label}
      </Box>
      <IconButton
        size="small"
        data-action-toggle
        aria-label={`Favorite ${job.label}`}
        aria-pressed={isFavorite}
        onClick={() => onToggleFavorite(job.token)}
        sx={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          ml: 'auto',
          color: isFavorite ? 'primary.main' : 'text.secondary',
          opacity: toggleOpacity,
          transition: 'opacity 150ms',
          '&:focus-visible': { opacity: 1, outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
          '@media (hover: none)': { opacity: 1 },
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {isFavorite ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
  );
}

export { JobRow };
export type { JobRowProps };
